import { Link } from '@tanstack/react-router';
import { CalendarDays } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { CalendarCreateButton } from '@/features/calendars/components/calendar-create-button';
import { useProjects } from '@/features/projects/use-projects';
import { useProjectFilterParam } from '@/lib/use-project-filter';
import { useCalendars } from '@/features/calendars/use-calendars';
import { hostname, relTime } from '@/lib/format';

const PROJECT_FILTER_STORAGE = 'seo:calendar-project';
const ALL_SENTINEL = '__all__';

export function CalendarsRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];

  // Filter lives in the URL (?project=) — see useProjectFilterParam.
  const { projectId, setProjectId } = useProjectFilterParam({
    storageKey: PROJECT_FILTER_STORAGE,
    projects: availableProjects,
    projectsLoading: projects.isLoading,
  });

  const cals = useCalendars(projectId || undefined);
  const items = cals.data?.items ?? [];
  const activeProject = availableProjects.find((p) => p.id === projectId);

  return (
    <Page>
      <PageHeader
        title="Content calendar"
        description="A month-by-month plan for what to publish, what to refresh, and how the pieces fit together — built from your latest audit and keyword research."
        actions={
          <CalendarCreateButton
            projects={availableProjects}
            defaultProjectId={projectId}
          />
        }
      />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project filter">
            {availableProjects.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>No projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select
                value={projectId || ALL_SENTINEL}
                onValueChange={(v) =>
                  setProjectId(v === ALL_SENTINEL ? '' : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SENTINEL}>All projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          {activeProject ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {hostname(activeProject.domain)}
            </div>
          ) : null}
        </Section>

        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                Your content plans
              </SectionTitle>
              <SectionDescription>
                Each calendar plans several months of new posts, supporting articles, and updates to existing pages.
              </SectionDescription>
            </div>
          </SectionHeader>

          {cals.error ? <ErrorBanner error={cals.error} /> : null}

          {cals.isLoading ? (
            <div className="flex justify-center p-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="Plan the next few months"
              description="Generate a calendar and we'll lay out which topics to publish first, what supporting posts to add, and when to refresh what you already have."
              variant="inline"
              action={
                <CalendarCreateButton
                  projects={availableProjects}
                  defaultProjectId={projectId}
                  label="Generate your first calendar"
                  buttonProps={{ size: 'sm' }}
                />
              }
            />
          ) : (
            <ul className="space-y-2">
              {items.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/calendars/$calendarId"
                    params={{ calendarId: c.id }}
                    className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                  >
                    <div>
                      <div className="font-mono text-sm group-hover:text-primary">
                        cal-{c.id.slice(0, 8)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.period_months} month
                        {c.period_months === 1 ? '' : 's'} ·{' '}
                        {c.capacity_per_month} pieces per month · created{' '}
                        {relTime(c.created_at)}
                      </div>
                    </div>
                    <StatusPill status={c.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </Page>
  );
}
